import { Localized, useLocalization } from "@fluent/react"
import { LoadingButton } from "@mui/lab"
import { Alert, Autocomplete, Box, Chip, Collapse, Container, FormControlLabel, IconButton, Paper, Skeleton, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { createEntityAdapter, EntityState } from "@reduxjs/toolkit"
import { Fragment, useCallback, useEffect, useState } from "react"
import { LoadCompanies } from "../../app/AppDataLoader"
import { AppId } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { If } from "../../app/If"
import { Company, selectAllCompanies } from "../company/companiesSlice"
import { showError } from "../notifications/notificationsSlice"
import { activateModule, deactivateModule, loadAllCompanyModules, ModuleActive, setCompanyPackageActive } from "./moduleSlice"
import { PackageName, packageLabel } from "../permissions/permissionsSlice"
import produce from "immer"
import { LoadingContainer } from "../../app/LoadingContainer"

const adapter = createEntityAdapter<ModuleActive>({
    selectId: m => m.moduleId,
})
const { selectAll } = adapter.getSelectors()

interface ModuleAccessTableProps {
    companyId: string | undefined;
    onSave?: () => void;
}

export const ModuleAccessTable = (props: ModuleAccessTableProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [activeModules, setActiveModules] = useState<EntityState<ModuleActive>>(adapter.getInitialState())
    const [activating, setActivating] = useState<Set<AppId>>(new Set())
    const [loading, setLoading] = useState(false)
    const [savingPackages, setSavingPackages] = useState<Set<PackageName>>(new Set())
    const [expanded, setExpanded] = useState<Set<AppId>>(new Set())

    const toggleExpanded = useCallback((moduleId: AppId) => {
        setExpanded(produce(draft => {
            if (draft.has(moduleId)) {
                draft.delete(moduleId)
            } else {
                draft.add(moduleId)
            }
        }))
    }, [])

    useEffect(() => {
        const loadActiveModules = async () => {
            if (props.companyId !== undefined) {
                setLoading(true)
                try {
                    return await dispatch(loadAllCompanyModules(props.companyId)).unwrap()
                } catch (error) {
                    return []        
                } finally {
                    setLoading(false)
                }
            }
            return []
        }
        loadActiveModules().then(x => setActiveModules(prev => adapter.setAll(prev, x)))
    }, [dispatch, props.companyId])

    const handleActivateClick = useCallback(async (moduleId: AppId, moduleCode: string) => {
        if (props.companyId !== undefined) {
            setActivating(produce(draft => {
                draft.add(moduleId)
            }))
            try {
                await dispatch(activateModule({
                    companyId: props.companyId,
                    moduleId,
                }))

                if(props.onSave) props.onSave();
            } catch (error) {
                showError("error")    
            }
            setActivating(produce(draft => {
                draft.delete(moduleId)
            }))
            setActiveModules(state => adapter.updateOne(state, {
                id: moduleId,
                changes: {
                    active: true,
                },
            }))
        }
    }, [dispatch, props.companyId])

    const handleDeactivateClick = useCallback(async (moduleId: AppId, moduleCode: string) => {
        if (props.companyId !== undefined) {
            setActivating(produce(draft => {
                draft.add(moduleId)
            }))
            try {
                await dispatch(deactivateModule({
                    companyId: props.companyId,
                    moduleId,
                }))
                if(props.onSave) props.onSave();
            } catch (error) {
                showError("error")    
            }
            setActivating(produce(draft => {
                draft.delete(moduleId)
            }))
            setActiveModules(state => adapter.updateOne(state, {
                id: moduleId,
                changes: {
                    active: false,
                },
            }))
        }
    }, [dispatch, props.companyId])

    // A package can belong to several modules but is one switch per company, so the refreshed list
    // from the server is applied as a whole.
    const handlePackageToggle = useCallback(async (pkg: PackageName, isActive: boolean) => {
        if (props.companyId === undefined) return
        setSavingPackages(produce(draft => {
            draft.add(pkg)
        }))
        try {
            const modules = await dispatch(setCompanyPackageActive({
                companyId: props.companyId,
                package: pkg,
                isActive,
            })).unwrap()
            setActiveModules(state => adapter.setAll(state, modules))
            if(props.onSave) props.onSave();
        } catch (error) {
            dispatch(showError("error"))
        } finally {
            setSavingPackages(produce(draft => {
                draft.delete(pkg)
            }))
        }
    }, [dispatch, props.companyId, props.onSave])

    return (
        <If condition={!loading} otherwise={<LoadingContainer/>}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '56px', }} />
                                <TableCell>
                                    <Localized id="module-name">Nazwa modułu</Localized>
                                </TableCell>
                                <TableCell sx={{ width: '140px', }}>
                                    <Localized id="module-is-active">Aktywny</Localized>
                                </TableCell>
                                <TableCell sx={{ width: '160px', }}>
                                    <Localized id="module-packages">Pakiety</Localized>
                                </TableCell>
                                <TableCell sx={{ width: '160px', }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectAll(activeModules).map(({ moduleId: id, description, active, code, packages = [] }) => {
                                const open = expanded.has(id)
                                const activePackages = packages.filter(p => p.isActive).length
                                return <Fragment key={id}>
                                    <TableRow sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
                                        <TableCell>
                                            <If condition={packages.length > 0}>
                                                <IconButton size="small" onClick={() => toggleExpanded(id)}>
                                                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </If>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>{description}</Typography>
                                            <Typography variant="caption" color="text.secondary">{code}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <If condition={active}
                                                otherwise={<Chip size="small" variant="outlined" label={<Localized id="module-inactive">Nieaktywny</Localized>} />}>
                                                <Chip size="small" color="success" label={<Localized id="active">Aktywny</Localized>} />
                                            </If>
                                        </TableCell>
                                        <TableCell>
                                            <If condition={packages.length > 0} otherwise={<Typography variant="body2" color="text.disabled">—</Typography>}>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color={active && activePackages > 0 ? "primary" : "default"}
                                                    onClick={() => toggleExpanded(id)}
                                                    label={<Localized id="module-packages-summary" vars={{ active: activePackages, total: packages.length }}>
                                                        {`${activePackages} / ${packages.length}`}
                                                    </Localized>} />
                                            </If>
                                        </TableCell>
                                        <TableCell>
                                            <If condition={active} 
                                                otherwise={<LoadingButton 
                                                                loading={activating.has(id)} 
                                                                variant="outlined" 
                                                                color="success"
                                                                onClick={() => handleActivateClick(id, code)}>
                                                                <Localized id="module-activate"><span>Aktywuj</span></Localized>
                                                            </LoadingButton>}>
                                                <LoadingButton 
                                                    loading={activating.has(id)} 
                                                    variant="outlined" 
                                                    color="error" 
                                                    onClick={() => handleDeactivateClick(id, code)}>
                                                    <Localized id="module-deactivate">
                                                        <span>Deaktywuj</span>
                                                    </Localized>
                                                </LoadingButton>
                                            </If>
                                        </TableCell>
                                    </TableRow>
                                    <If condition={packages.length > 0}>
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ paddingY: 0 }}>
                                                <Collapse in={open} timeout="auto" unmountOnExit>
                                                    <Box sx={{ marginLeft: 7, marginRight: 2, marginY: 2 }}>
                                                        <Typography variant="overline" color="text.secondary">
                                                            <Localized id="module-packages">Pakiety</Localized>
                                                        </Typography>
                                                        <If condition={!active}>
                                                            <Alert severity="info" sx={{ marginBottom: 1 }}>
                                                                <Localized id="package-inactive-module-hint">Aktywuj moduł, aby zarządzać jego pakietami.</Localized>
                                                            </Alert>
                                                        </If>
                                                        <Box sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                                            columnGap: 2,
                                                        }}>
                                                            {packages.map(({ package: pkg, isActive }) =>
                                                                <FormControlLabel
                                                                    key={pkg}
                                                                    label={packageLabel(l10n, pkg)}
                                                                    disabled={!active || savingPackages.has(pkg)}
                                                                    control={<Switch
                                                                        size="small"
                                                                        checked={isActive}
                                                                        onChange={(_, checked) => handlePackageToggle(pkg, checked)} />} />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </If>
                                </Fragment>
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </If>
    )
}

export const ModuleAccess = () => {
    const allCompanies = useAppSelector(selectAllCompanies)

    const [company, setCompany] = useState<Company | undefined>(undefined)

    const handleCompanyChange = useCallback((_: any, newCompany: Company | null) => {
        if (newCompany) {
            setCompany(newCompany)
        }
    }, [])

    return <Container component={Paper} sx={{
        padding: 4,
    }}>
        <Stack spacing={4}>
            <LoadCompanies component={<Skeleton />}>
                <Autocomplete 
                    sx={{
                        width: "400px",
                    }}
                    fullWidth={false}
                    options={allCompanies}
                    getOptionLabel={({ name }) => name}
                    value={company ?? null}
                    onChange={handleCompanyChange}
                    renderInput={params =>
                        <TextField {...params} />
                    }
                />
            </LoadCompanies> 
            <ModuleAccessTable companyId={company?.id} />
        </Stack>
    </Container>
}
